import {
  Box,
  Card,
  Text,
  Select,
  InlineStack,
  ButtonGroup,
  Button,
  Icon,
  Divider,
  Page,
  FormLayout,
  Grid,
  Spinner,
  Tooltip,
  Avatar,
  RangeSlider,
  DataTable,
  Badge,
  Thumbnail,
  ProgressBar,
} from "@shopify/polaris";
import {
  CategoriesIcon,
  EditIcon,
  ListBulletedIcon,
  PlusCircleIcon,
  TeamIcon,
} from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import ProjectCard from "../../Components/ProjectCard";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import axios from "../../Assets/Lib/axios";
import emptyListImage from "../../Assets/Images/emptyList.svg";

const MainProjects = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("All");
  const [health, setHealth] = useState("All");
  const [sort, setSort] = useState("Name");
  const [viewMode, setViewMode] = useState("grid");
  const [layout, setLayout] = useState("Expanded");
  const handleAddItem = () => {
    navigate("/admin/projects/new");
  };

  const [country, setCountry] = useState("");
  const [countryButtons, setCountryButtons] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "" },
          { label: "Mutual", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
            flag: country.flag,
          })),
        ];
        setCountryButtons(options);
      })
      .catch((err) => console.log(err));
  }, []);
  const [projects, setProjects] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, [type, status, country, health]);
  const fetchProjects = async () => {
    //setIsLoading(true);
    try {
      const params = new URLSearchParams();

      if (type !== "All") {
        params.append("filter[project_type_id]", type);
      }
      if (status !== "All") {
        params.append("filter[status]", status);
      }
      if (health !== "All") {
        params.append("filter[health]", health);
      }
      if (country !== "") {
        params.append("filter[country]", country);
      }

      const response = await axios.get(`/projects`, { params });
      const data = response.data.data.data || [];
      setProjects(data);

      setItems(
        data.map((item, index) => [
          <div>
            <Thumbnail
              source={
                item.logo ||
                "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png"
              }
              size="extraSmall"
            />
          </div>,
          <div>{item.name || "-"}</div>,
          <div>
            <Badge
              tone={
                String(item.status.id) === "2"
                  ? "warning"
                  : String(item.status.id) === "3"
                    ? "info"
                    : String(item.status.id) === "4"
                      ? "success"
                      : "default"
              }
            >
              {item.status.description}
            </Badge>
          </div>,
          <div>{item.start_date ? flipDate(item.start_date) : "-"}</div>,
          <div>{item.deadline || "-"}</div>,
          // <div>{item.project_progress}</div>,
          <div>
            <ProgressBar
              progress={item.project_progress}
              size="small"
              tone="success"
            />
          </div>,
          // <div>{item.client_satisfaction || "-"}</div>,
          // <div>{item.current_cost || "-"}</div>,
          // <div>{item.current_profit || "-"}</div>,

          <div className="me-3">
            <ButtonGroup>
              <Button
                onClick={() => navigate(`/admin/projects/${item.id}`)}
                icon={EditIcon}
              >
                Edit
              </Button>
            </ButtonGroup>
          </div>,
        ]),
      );

      // setTotalPages(response.data.data.total || 1);
    } catch (error) {
      console.error(error);
    }
    // setIsLoading(false);
  };
  const [optionsTypes, setOptionsTypes] = useState([]);
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`/project-types`);
        const data = response?.data?.data?.data;

        const options = [
          { label: "All", value: "All" },
          ...data.map((dept) => ({
            label: dept.name,
            value: dept.id,
          })),
        ];

        setOptionsTypes(options);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchTypes();
  }, []);
  const [view, setView] = useState("Months"); // Days | Weeks | Months

  const milestones = useMemo(() => {
    if (!projects?.length) return [];
    return projects.map((p) => ({
      label: p.name,
      date: p.deadline,
      logo: p.logo,
      status: p.status?.description || "Unknown",
    }));
  }, [projects]);

  // Timeline boundaries
  const startDate = useMemo(() => {
    if (!projects?.length) return new Date();
    const allStarts = projects.map((p) => new Date(p.start_date));
    return new Date(Math.min(...allStarts));
  }, [projects]);

  const endDate = useMemo(() => {
    if (!projects?.length) return new Date();
    const allEnds = projects.map((p) => new Date(p.deadline));
    return new Date(Math.max(...allEnds));
  }, [projects]);
  const today = new Date();

  // Determine total timeline days (span)
  const totalDays = useMemo(
    () => (endDate - startDate) / (1000 * 60 * 60 * 24),
    [startDate, endDate],
  );

  // Function to calculate relative position %
  const getPosition = (dateString) => {
    const date = new Date(dateString);
    const diffDays = (date - startDate) / (1000 * 60 * 60 * 24);
    return `${(diffDays / totalDays) * 100}%`;
  };

  // Zoom scale multiplier
  const scale = useMemo(() => {
    switch (view) {
      case "Days":
        return 6;
      case "Weeks":
        return 2;
      case "Months":
      default:
        return 1;
    }
  }, [view]);
  const [mode, setMode] = useState("List");
  const [zoom, setZoom] = useState(100); // % zoom (100 = default)
  const flipDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };
  return projects ? (
    <Page
      title="Projects"
      fullWidth
      primaryAction={
        <Button variant="primary" onClick={handleAddItem} icon={PlusCircleIcon}>
          Add Project
        </Button>
      }
      secondaryActions={
        <ButtonGroup>
          <Button onClick={() => navigate("/admin/team")} icon={TeamIcon}>
            Team
          </Button>
        </ButtonGroup>
      }
    >
      <FormLayout>
        <Card>
          <Box padding="300">
            <InlineStack align="space-between" blockAlign="center" wrap={false}>
              {/* Filters Section */}
              <InlineStack gap="300" blockAlign="center">
                <Select
                  labelInline
                  options={optionsTypes}
                  value={type}
                  onChange={setType}
                  label="Type: "
                />
                <Select
                  labelInline
                  options={[
                    { label: "All", value: "All" },
                    { label: "Pending", value: "1" },
                    { label: "In Progress", value: "2" },
                    { label: "QA", value: "3" },
                    { label: "Done", value: "4" },
                  ]}
                  value={status}
                  onChange={setStatus}
                  label="Status: "
                />
                <Select
                  labelInline
                  options={[
                    { label: "All", value: "All" },
                    { label: "Good", value: "1" },
                    { label: "Warning", value: "2" },
                    { label: "Critical", value: "3" },
                  ]}
                  value={health}
                  onChange={setHealth}
                  label="Health: "
                />
                {/* <Select
                  labelHidden
                  options={["Name", "Date", "Newest"]}
                  value={sort}
                  onChange={setSort}
                  suffix={<Text as="span">↕</Text>}
                /> */}
              </InlineStack>

              {/* View + Layout Section */}
              <InlineStack gap="400" blockAlign="center">
                {/* <ButtonGroup variant="segmented">
                <Button
                  pressed={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                  icon={ListBulletedIcon}
                />
                <Button
                  pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                  icon={ListBulletedIcon}
                />
              </ButtonGroup> */}

                {mode === "Grid" ? (
                  <ButtonGroup variant="segmented">
                    {["Compact", "Normal", "Expanded"].map((label) => (
                      <Button
                        key={label}
                        pressed={layout === label}
                        onClick={() => setLayout(label)}
                      >
                        {label}
                      </Button>
                    ))}
                  </ButtonGroup>
                ) : null}
                <ButtonGroup variant="segmented">
                  {["Grid", "List"].map((label) => (
                    <Button
                      key={label}
                      pressed={mode === label}
                      onClick={() => setMode(label)}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
                {/* {countryButtons.map((t) => (
                  <Button
                    key={t.value}
                    pressed={country === t.value}
                    onClick={() => setCountry(String(t.value))}
                  >
                    {t.flag && (
                      <img
                        src={t.flag}
                        alt={t.flag}
                        style={{ width: 20, marginRight: 6 }}
                      />
                    )}
                    {t.label}
                  </Button>
                ))} */}
              </InlineStack>
            </InlineStack>
          </Box>
        </Card>
        <Card>
          <InlineStack align="start">
            <ButtonGroup>
              {countryButtons.map((t) => (
                <Button
                  key={t.value}
                  pressed={country === t.value}
                  onClick={() => setCountry(String(t.value))}
                >
                  {t.flag && (
                    <img
                      src={t.flag}
                      alt={t.flag}
                      style={{ width: 20, marginRight: 6 }}
                    />
                  )}
                  {t.label}
                </Button>
              ))}
            </ButtonGroup>
          </InlineStack>
        </Card>
        <Card>
          <Box padding="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingLg" as="h5">
                Project Timeline
              </Text>
              <ButtonGroup segmented>
                {["Days", "Weeks", "Months"].map((v) => (
                  <Button
                    key={v}
                    pressed={view === v}
                    onClick={() => setView(v)}
                  >
                    {v}
                  </Button>
                ))}
              </ButtonGroup>
              <Box maxWidth="300px" marginBlockStart="300">
                <RangeSlider
                  label="Zoom"
                  value={zoom}
                  min={0}
                  max={200}
                  step={10}
                  onChange={setZoom}
                  output
                />
              </Box>
            </InlineStack>

            <Box
              // paddingBlockStart="600"
              // paddingBlockEnd="400"
              style={{
                position: "relative",
                overflowX: "auto",
                whiteSpace: "nowrap",
                height: "200px",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  height: "6px",
                  width: `${100 * scale * (zoom / 100)}%`,

                  backgroundColor: "#E4E7EB",
                  borderRadius: "3px",
                }}
              />

              {milestones.map((m, i) => {
                const date = new Date(m.date);
                const percent =
                  ((date - startDate) / (endDate - startDate)) * 100;

                const colorMap = {
                  Good: "#008060",
                  Warning: "#F49342",
                  Critical: "#E13219",
                };
                const circleColor = colorMap[m.status] || "#5C5F62";

                const hasLogo = !!m.logo;
                const imageSrc = hasLogo ? m.logo : null;

                return (
                  <div
                    key={i}
                    style={{
                      position: "absolute",
                      left: `calc(${Math.max(0, Math.min(100, percent))}% * ${
                        scale * (zoom / 100)
                      })`,

                      top: "calc(50% - 24px)", // centers the image box vertically on the line (48px / 2)
                      // transform: "translateX(-50%)",
                      marginTop: "3rem",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      textAlign: "center",
                    }}
                  >
                    <Tooltip content={`${m.label}`}>
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "#fff",
                          borderRadius: "12px",
                          boxShadow:
                            "0 2px 6px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "10px",
                          overflow: "hidden",
                          // border: `2px solid ${circleColor}`,
                        }}
                      >
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={m.label}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span
                            style={{ fontSize: "20px", color: circleColor }}
                          ></span>
                        )}
                      </div>
                    </Tooltip>
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      {new Date(m.date).toLocaleString("default", {
                        month: "short",
                        day: "numeric",
                      })}
                    </Text>
                    <Text tone="subdued" variant="bodySm" as="p">
                      {new Date(m.date).getFullYear()}
                    </Text>
                  </div>
                );
              })}

              {today >= startDate && today <= endDate && (
                <div
                  style={{
                    position: "absolute",
                    left: `calc(${
                      ((today - startDate) / (endDate - startDate)) * 100
                    }% * ${scale * (zoom / 100)})`,

                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 3,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "0",
                      height: "100px",
                      borderLeft: "3px solid #008060",
                      marginBottom: "8px",
                    }}
                  ></div>
                  <Text tone="success" fontWeight="bold">
                    Today
                  </Text>
                </div>
              )}
            </Box>

            <Box marginBlockStart="400">
              <InlineStack align="space-between">
                <Text tone="subdued" variant="bodySm">
                  {startDate.toLocaleDateString("default", {
                    month: "short",
                    year: "numeric",
                  })}
                </Text>
                <Text tone="subdued" variant="bodySm">
                  {endDate.toLocaleDateString("default", {
                    month: "short",
                    year: "numeric",
                  })}
                </Text>
              </InlineStack>
            </Box>
          </Box>
        </Card>

        <>
          {projects?.length ? (
            mode === "Grid" ? (
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))", // ✅ prevents overflow
                  gap: "1rem",
                  width: "100%", // ✅ full width
                  boxSizing: "border-box", // ✅ includes padding/borders in width calc
                  overflowX: "hidden", // ✅ hides any rounding overflow
                }}
              >
                {/* <Box
               style={{
               display: "grid",
               gridTemplateColumns: "repeat(3, 1fr)", // ✅ always 3 columns
                 justifyContent: "start", // ✅ keeps centered if < 3
                 gap: "1rem",
               }}
             > */}
                {projects.map((p, i) => (
                  <Card key={i}>
                    <Box
                      padding="400"
                      display="flex"
                      flexDirection="column"
                      height="100%"
                    >
                      <ProjectCard project={p} mode={layout.toLowerCase()} />
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <Card padding={0}>
                <DataTable
                  columnContentTypes={["text", "text", "text", "text"]}
                  headings={[
                    "Logo",
                    "Name",
                    "Satus",
                    "Start Date",
                    "Deadline",
                    "Progress",
                    // "Client Satisfaction",
                    // "Cost",
                    // "Current Profit",
                    "",
                  ]}
                  rows={items}
                  verticalAlign="middle"
                  stickyHeader
                  // footerContent={
                  //   items?.length &&
                  //   `Showing ${items.length} of ${totalPages} results`
                  // }
                />
              </Card>
            )
          ) : (
            // <Box paddingBlock="400" paddingInline="400">
            //   <Grid
            //     columns={{
            //       xs: 1,
            //       sm: 2,
            //       lg: 3, // 3 cards per row on desktop
            //     }}
            //     gap="400"
            //   >
            //     {projects.map((p, i) => (
            //       <Grid.Cell key={i}>
            //         <Box
            //           width="100%"
            //           minWidth="350px" // 👈 ensures card width like your screenshots
            //         >
            //           <ProjectCard project={p} mode={layout.toLowerCase()} />
            //         </Box>
            //       </Grid.Cell>
            //     ))}
            //   </Grid>
            // </Box>
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "25%" }}>
                  <img src={emptyListImage} alt="No results" />
                </div>
                <div className="mb-3">
                  <Text variant="headingLg" as="h5" color="subdued">
                    No results found
                  </Text>
                </div>
              </div>
            </Card>
          )}
        </>
      </FormLayout>
    </Page>
  ) : (
    <div className="spinnerContainer">
      <div className="vertical-center">
        <Spinner size="large" />
      </div>
    </div>
  );
};

export default MainProjects;
